/**
 * Очередь всплывающих модалок обновлений («Что нового»).
 *
 * Гарантия «каждый анонс всплывает ровно 1 раз» держится на СЕРВЕРЕ
 * (`Announcement.popup_seen_at`): при показе модалки фронт бьёт
 * `POST /announcements/{id}/seen`, и в следующей ленте карточка приходит уже
 * с непустым `popup_seen_at` — в очередь она больше не попадёт. Этот slice —
 * лишь эфемерная очередь id к показу. Единственный источник наполнения —
 * `UpdatesPopup`, который по ленте (`listAnnouncements`) кладёт в очередь
 * непоказанные анонсы: и оффлайн-догон при загрузке (увидит всё, что
 * пропустил), и онлайн-показ — WS-мост на `announcement_published` инвалидирует
 * ленту, рефетч приносит новую карточку, и тот же useEffect докидывает её id.
 * Напрямую из WS-моста id НЕ добавляем: карточки тогда ещё нет в ленте, и
 * нерезолвимую голову «мёртвый id»-эффект выкидывал в гонке (см. rtkBridge).
 *
 * `enqueuedIds` — анти-дубль в пределах сессии: пока лента дорефетчивается
 * после `seen`, карточка может секунду мелькать как непоказанная. Раз
 * добавив id, второй раз в очередь его не пустим — чтобы всплывашка не
 * задвоилась и не «мигала».
 *
 * Здесь НЕТ тела/заголовка анонса: единственный источник правды — лента в
 * RTK Query (`listAnnouncements`). Очередь хранит только id, а `UpdatesPopup`
 * резолвит его в свежую карточку (с `body`) из кэша ленты.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

interface State {
  /** FIFO id непоказанных всплывашек. Показывается голова (`[0]`). */
  popupQueue: string[];
  /** id, когда-либо попадавшие в очередь за сессию (анти-дубль). */
  enqueuedIds: Record<string, true>;
}

const initialState: State = { popupQueue: [], enqueuedIds: {} };

const slice = createSlice({
  name: 'announcements',
  initialState,
  reducers: {
    /**
     * Добавить анонсы в очередь показа (в конец, с сохранением порядка).
     * Идемпотентно: id, который уже проходил через очередь в этой сессии,
     * повторно не добавляется. Принимает пачку id из ленты (`UpdatesPopup`) —
     * и стартовый оффлайн-догон, и онлайн-показ после WS-рефетча идут этим же
     * путём (карточки к моменту вызова уже есть в ленте).
     */
    announcementsEnqueued(state, action: PayloadAction<string[]>) {
      for (const id of action.payload) {
        if (state.enqueuedIds[id]) continue;
        state.enqueuedIds[id] = true;
        state.popupQueue.push(id);
      }
    },
    /** Снять текущую всплывашку (голову) — показывается следующая из очереди. */
    announcementPopupDismissed(state) {
      state.popupQueue.shift();
    },
    /** Очистить всю очередь показа при закрытии общей модалки «Что нового». */
    announcementPopupsCleared(state) {
      state.popupQueue = [];
    },
    /**
     * Смена пользователя во вкладке (выход/вход): очередь и анти-дубль —
     * сессионные, следующему пользователю они не принадлежат.
     */
    announcementsReset() {
      return initialState;
    },
  },
});

export const {
  announcementsEnqueued,
  announcementPopupDismissed,
  announcementPopupsCleared,
  announcementsReset,
} = slice.actions;
export default slice.reducer;
